import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, MicOff, Volume2 } from 'lucide-react';
import { cn, ui } from '../../utils';
import { getSelectedVoiceId } from './VoicePicker';

interface VoiceButtonProps {
  onAudioReady?: (file: File) => void | Promise<void>;
  onTranscription?: (text: string) => void | Promise<void>;
  agentResponse?: string;
  voiceEnabled?: boolean;
  disabled?: boolean;
  className?: string;
  showLabel?: boolean;
}

function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  return '';
}

export function VoiceButton({
  onAudioReady,
  onTranscription,
  agentResponse,
  voiceEnabled = false,
  disabled,
  className,
  showLabel = false,
}: VoiceButtonProps) {
  const [state, setState] = useState<'idle' | 'listening' | 'thinking' | 'speaking'>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const spokenResponseRef = useRef<string | null>(null);
  const synthesisAbortRef = useRef<AbortController | null>(null);

  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const cleanupAudio = () => {
    synthesisAbortRef.current?.abort();
    synthesisAbortRef.current = null;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  };

  const transcribeRecording = async (file: File) => {
    if (onTranscription) {
      const formData = new FormData();
      formData.append('audio', file, file.name);

      const response = await fetch('/api/voice/transcribe', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.success || !payload?.text) {
        throw new Error(payload?.error || 'Transcription failed');
      }

      await Promise.resolve(onTranscription(payload.text));
      return;
    }

    if (onAudioReady) {
      await Promise.resolve(onAudioReady(file));
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      console.warn('Audio recording is not available in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      setState('listening');

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        cleanupStream();
        setState('idle');
      };

      recorder.onstop = async () => {
        cleanupStream();
        setState('thinking');

        try {
          const blobType = recorder.mimeType || mimeType || 'audio/webm';
          const extension = blobType.includes('mp4') ? 'm4a' : 'webm';
          const blob = new Blob(chunksRef.current, { type: blobType });
          const file = new File([blob], `voice-note-${Date.now()}.${extension}`, { type: blobType });
          await transcribeRecording(file);
        } catch (error) {
          console.error('Failed to process voice note:', error);
        } finally {
          setState('idle');
        }
      };

      recorder.start();
    } catch (error) {
      console.error('Mic access denied:', error);
      cleanupStream();
      setState('idle');
    }
  };

  const stopListening = () => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  };

  const stopSpeaking = () => {
    cleanupAudio();
    setState('idle');
  };

  const speakResponse = async (text: string) => {
    if (!text) return;

    cleanupAudio();
    const controller = new AbortController();
    synthesisAbortRef.current = controller;
    spokenResponseRef.current = text;
    setState('speaking');

    try {
      const response = await fetch('/api/voice/synthesize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({ text, voice: getSelectedVoiceId() }),
      });

      if (!response.ok) {
        throw new Error('Voice synthesis failed');
      }

      const blob = await response.blob();
      if (controller.signal.aborted) {
        return;
      }

      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audioUrlRef.current = url;

      const finish = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
        if (audioUrlRef.current === url) {
          URL.revokeObjectURL(url);
          audioUrlRef.current = null;
        }
        if (!controller.signal.aborted) {
          setState('idle');
        }
      };

      audio.onended = finish;
      audio.onerror = finish;
      await audio.play();
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') {
        console.error('TTS failed:', error);
      }
      if (!controller.signal.aborted) {
        setState('idle');
      }
    } finally {
      if (synthesisAbortRef.current === controller) {
        synthesisAbortRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!voiceEnabled) {
      return;
    }

    if (agentResponse && state === 'idle' && spokenResponseRef.current !== agentResponse) {
      void speakResponse(agentResponse);
    }
  }, [agentResponse, state, voiceEnabled]);

  useEffect(() => {
    if (voiceEnabled) {
      return;
    }

    cleanupAudio();
    setState((current) => (current === 'speaking' ? 'idle' : current));
  }, [voiceEnabled]);

  useEffect(
    () => () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
      cleanupStream();
      cleanupAudio();
    },
    [],
  );

  const handleClick = () => {
    if (state === 'listening') {
      stopListening();
      return;
    }
    if (state === 'speaking') {
      stopSpeaking();
      return;
    }
    if (state === 'idle') {
      void startRecording();
    }
  };

  const buttonLabel = state === 'idle'
    ? 'Voice'
    : state === 'listening'
      ? 'Stop listening'
      : state === 'speaking'
        ? 'Stop speaking'
        : 'Processing...';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || state === 'thinking'}
      className={cn(
        ui.button.icon,
        showLabel && 'w-full justify-start gap-3 px-3',
        state === 'idle' && 'text-muted-foreground hover:bg-background hover:text-foreground',
        state === 'listening' && 'animate-pulse bg-red-500/10 text-red-400',
        state === 'thinking' && 'bg-amber-500/10 text-amber-400',
        state === 'speaking' && 'animate-pulse bg-emerald-500/10 text-emerald-400',
        className,
      )}
      title={
        state === 'idle'
          ? 'Speak your message'
          : state === 'listening'
            ? 'Stop listening'
            : state === 'speaking'
              ? 'Stop speaking'
              : 'Processing...'
      }
    >
      {state === 'idle' ? <Mic className="h-4 w-4" /> : null}
      {state === 'listening' ? <MicOff className="h-4 w-4" /> : null}
      {state === 'thinking' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {state === 'speaking' ? <Volume2 className="h-4 w-4" /> : null}
      {showLabel ? <span className="text-sm font-medium">{buttonLabel}</span> : null}
    </button>
  );
}

export default VoiceButton;
